"use client";

import { GoogleTagManager } from "@next/third-parties/google";
import { AnimatePresence, motion } from "framer-motion";
import { Cookie, Settings, Shield } from "lucide-react";
import { useEffect, useState } from "react";

import { Button } from "@/components/ui/button";

export default function CookieConsent() {
  const [showBanner, setShowBanner] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [gaAccepted, setGaAccepted] = useState(false);

  useEffect(() => {
    const consent = localStorage.getItem("cookie-consent");

    if (!consent) {
      const timer = setTimeout(() => setShowBanner(true), 1200);
      return () => clearTimeout(timer);
    } else if (consent === "accepted") {
      setGaAccepted(true);
    }
  }, []);

  const handleAccept = () => {
    localStorage.setItem("cookie-consent", "accepted");
    localStorage.setItem("cookie-consent-date", new Date().toISOString());
    setGaAccepted(true);
    setShowBanner(false);
  };

  const handleDecline = () => {
    localStorage.setItem("cookie-consent", "declined");
    localStorage.setItem("cookie-consent-date", new Date().toISOString());
    setShowBanner(false);
  };

  const handleCustomize = () => setShowDetails(!showDetails);

  if (!showBanner) return null;

  return (
    <>
      <div className="fixed inset-0 z-50 bg-black/40" />
      <AnimatePresence>
        {showBanner && (
          <motion.div
            initial={{ y: 150, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 150, opacity: 0 }}
            transition={{ type: "spring", stiffness: 80 }}
            className="fixed inset-x-0 bottom-0 z-50 flex justify-center px-4 pb-4"
          >
            <div className="w-full max-w-4xl rounded-2xl border bg-white p-6 shadow-xl">
              <div className="mb-4 flex items-center gap-2">
                <div className="bg-secondary/20 rounded-lg p-2">
                  <Cookie
                    className="text-secondary h-6 w-6"
                    aria-hidden="true"
                  />
                </div>
                <h2 className="text-lg font-semibold text-gray-900">
                  Política de Cookies
                </h2>
              </div>
              <div className="flex items-start gap-4">
                <div className="flex-1 space-y-4">
                  <p className="leading-relaxed text-gray-600">
                    Usamos cookies para melhorar sua experiência no site da São
                    Chico, entender como você navega e oferecer conteúdo
                    relevante. Você pode aceitar, recusar ou personalizar sua
                    decisão.
                  </p>

                  {/* Detalhes */}
                  <AnimatePresence>
                    {showDetails && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        className="space-y-3 border-t pt-4"
                        id="cookie-details"
                      >
                        <h3 className="flex items-center gap-2 font-medium text-gray-900">
                          <Shield
                            className="text-secondary h-4 w-4"
                            aria-hidden="true"
                          />
                          Como usamos seus cookies
                        </h3>

                        <ul className="ml-6 space-y-1 text-sm text-gray-600">
                          <li>
                            • <strong>Essenciais:</strong> Garantem que o site
                            funcione corretamente.
                          </li>
                          <li>
                            • <strong>Analíticos:</strong> Nos ajudam a entender
                            como você navega para melhorar sua experiência.
                          </li>
                          <li>
                            • <strong>Funcionais:</strong> Lembram preferências
                            e recursos ativados.
                          </li>
                        </ul>
                      </motion.div>
                    )}
                  </AnimatePresence>

                  {/* Botões */}
                  <div className="flex w-full flex-col items-center justify-between gap-3 pt-2 sm:flex-row">
                    <div className="pb-4 md:pb-0">
                      <button
                        onClick={handleCustomize}
                        className="text-secondary hover:text-secondary/80 flex cursor-pointer items-center justify-center gap-1 pt-1 text-sm underline"
                        aria-expanded={showDetails}
                        aria-controls="cookie-details"
                      >
                        <Settings className="h-4 w-4" aria-hidden="true" />
                        {showDetails ? "Ocultar Detalhes" : "Ver Detalhes"}
                      </button>
                    </div>

                    <div className="flex gap-4">
                      <Button onClick={handleAccept} size="lg">
                        Aceitar Todos
                      </Button>

                      <Button
                        onClick={handleDecline}
                        variant="outline"
                        size="lg"
                        className="flex-1 border-gray-300 text-gray-800 hover:bg-transparent hover:text-gray-600"
                      >
                        Recusar
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {gaAccepted && (
        <>
          <GoogleTagManager gtmId={process.env.NEXT_PUBLIC_GTM_ID || ""} />
        </>
      )}
    </>
  );
}
